import React, { useContext, useEffect } from 'react';

import { Autocomplete } from '@material-ui/lab';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, Typography } from '@material-ui/core';
import { FiChevronDown } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import debounce from 'lodash/debounce';

import { Employee } from '@vyce/core/src/types';
import { AppTextField } from '@vyce/core/src/components/inputs/AppTextField';
import { appAutocompleteUseStyles } from '@vyce/core/src/components/controlled-inputs/ControlledAutocomplete';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { IsVerified } from '@vyce/core/src/components/IsVerified';
import { WorkerLookupDTO } from '../../../api/types';

interface Props {
  employee?: Partial<Employee>;
  employees: Partial<Employee | WorkerLookupDTO>[];
  loading?: boolean;
  onSearch: (substring: string) => void;
  margin?: any;
  label?: string;
  rules?: any;
  idField?: string;
  fieldName?: string;
}

export const SearchEmployeeAutocomplete: React.FC<Props> = ({
  employee,
  employees,
  onSearch,
  loading,
  margin,
  label = 'Employees',
  rules = { required: 'Employee is required' },
  idField = 'uuid',
  fieldName = 'employee_id',
}) => {
  const classes = appAutocompleteUseStyles({ withoutLabel: !label });
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { isMobile } = useContext(DeviceContext);

  const handleSearch = debounce((event: any) => {
    const value = event.target.value;
    onSearch(value);
  }, 500);

  useEffect(() => {
    onSearch('');
  }, []);

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={rules}
      render={props => (
        <Autocomplete
          onChange={(e, newValue) => {
            props.field.onChange(newValue ? (newValue as any)[idField] : undefined);
          }}
          fullWidth
          disablePortal={isMobile}
          popupIcon={<FiChevronDown size="25px" color="#A0A3BD" />}
          closeIcon={<IoClose size="25px" color="#A0A3BD" />}
          defaultValue={employee}
          noOptionsText="Not Found"
          classes={classes}
          getOptionSelected={(option, value) => (option as any)[idField] === (value as any)[idField]}
          getOptionLabel={option => `${option.first_name} ${option.last_name}`}
          options={employees}
          filterOptions={options => options}
          renderOption={option => {
            const typedOption = option as Partial<Employee>;
            return (
              <Box display="flex" cy-test-id="search-employee" width="100%" justifyContent="space-between">
                <Typography variant="subtitle2">
                  {typedOption.first_name} {typedOption.last_name}{' '}
                  {typedOption.ni_number && `(${typedOption.ni_number})`}
                </Typography>
                {typedOption.rtw_verified !== undefined && (
                  <Box display="flex">
                    <Box display="flex" alignItems="center">
                      <Typography variant="caption">RTW - </Typography>
                      <IsVerified verified={!!typedOption?.rtw_verified} />
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Typography variant="caption">ID - </Typography>
                      <IsVerified verified={!!typedOption?.id_verified} />
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Typography variant="caption">CIS - </Typography>
                      <IsVerified verified={!!typedOption?.cis_verified} />
                    </Box>
                  </Box>
                )}
              </Box>
            );
          }}
          loading={loading}
          renderInput={params => (
            <AppTextField
              {...params}
              margin={margin}
              error={!!errors[fieldName]?.message}
              helperText={(errors[fieldName]?.message as string) || ''}
              onChange={handleSearch}
              placeholder="Start typing"
              label={label}
              cy-test-id="search-employees"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <Box position="absolute" right={80} top={18}>
                        <CircularProgress color="secondary" size={20} />
                      </Box>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
